import { makeStyles } from '@material-ui/styles'
import { ValidatedFormProps } from 'pf9-ui-components/built/components/validatedForm/model'
import withFormContext from 'pf9-ui-components/built/components/validatedForm/withFormContext'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React, { FC, useCallback, useMemo } from 'react'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { storagePoolsSelector } from 'app/plugins/openstack/components/storage/pools/selectors'
import { useAppSelector } from 'app/store'
import { compose, noop } from 'app/utils/fp'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import NodePoolConfigCard from './NodePoolConfigCard'

interface MasterServerConfigurationFieldsProps extends ValidatedFormProps {
  params: any
  getParamsUpdater: (key: string) => (value: unknown) => void
  nodePoolErrors: NodePoolErrors
}

export interface NodePoolErrors {
  [id: string]: { fieldId: string; message: string }
}

const MasterServerConfigurationFields = ({
  params,
  getParamsUpdater,
  nodePoolErrors = undefined,
}: MasterServerConfigurationFieldsProps) => {
  const classes = useStyles()
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors, {
    params,
  })
  // Filter out unsupported VM Flavors based on resource specs
  const flavors = useAppSelector(flavorsSelector)
  const filteredFlavors = useMemo(
    () => flavors?.filter((vm) => vm?.vcpus > 0 && vm?.ram > 0),
    [flavors],
  )

  // Storage Pools
  const storagePools = useAppSelector(storagePoolsSelector)

  // Networks
  const { loading: loadingNetworks } = useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)

  // Filter networks by tenant
  const networks = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  const handleNodePoolChange = useCallback(
    (id) => (newValues) => {
      const updatedNodePools = params?.masterNodePools?.map((nodePool) => {
        if (nodePool?.id === id) {
          return { ...nodePool, ...newValues }
        }
        return nodePool
      })

      getParamsUpdater('masterNodePools')(updatedNodePools)
    },
    [params?.masterNodePools, getParamsUpdater],
  )

  return (
    <div>
      {params?.masterNodePools?.map((nodePool) => {
        const id = nodePool?.id
        return (
          <NodePoolConfigCard
            key={id}
            nodePool={nodePool}
            onChange={handleNodePoolChange(id)}
            vmFlavors={filteredFlavors}
            storagePools={storagePools}
            networks={networks}
            showDeleteButton={false}
            onDeleteNodePool={noop}
            errors={nodePoolErrors?.[id]}
            loadingVmFlavors={loadingVmFlavors}
            getParamsUpdater={getParamsUpdater}
            params={params}
            isMasterNodePool={true}
          />
        )
      })}
    </div>
  )
}

export default compose(withFormContext)(
  MasterServerConfigurationFields,
) as FC<MasterServerConfigurationFieldsProps>

const useStyles = makeStyles<Theme>((theme) => ({
  addNodePoolButton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    margin: theme.spacing(4, 0),
    alignItems: 'center',
  },
  plusIcon: {
    color: theme.palette.blue[700],
    marginRight: theme.spacing(1),
    fontWeight: 900,
  },
}))
